import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
// import { useAuth } from "../hooks/chat/useAuth";
import { useWalletConnect } from "../hooks/useWalletConnect";
import { LoadingScreen } from "../components/LoadingScreen";
import { formatWalletAddress } from "../utils/wallet";
// import { storage } from "../lib/storage";
// import { notify } from "../utils/toast";
// import Marquee from "react-fast-marquee";
import { Typewriter } from "react-simple-typewriter";
import { ChevronDown, ChevronUp, Wallet } from "lucide-react";
// No need for hardcoded wallet descriptions anymore
// We'll use the descriptions from wallet metadata

export const LandingPage = () => {
  const {
    state: { loading, error },
    clearError,
  } = useAuth();
  const {
    connectWallet,
    disconnectWallet,
    authenticateWallet,
    connecting,
    authenticating,
    activeWallet,
    accounts,
    balance,
    minBalance,
    availableWallets,
  } = useWalletConnect();

  // Map available wallets to the format expected by the UI
  const walletOptions = React.useMemo(() => {
    return (
      availableWallets?.map((wallet) => {
        // Get the wallet ID in lowercase for consistent lookup
        const walletId =
          typeof wallet.id === "string" ? wallet.id.toLowerCase() : wallet.id;

        return {
          id: walletId,
          name: wallet.metadata?.name || wallet.id,
          icon: wallet.metadata?.icon || null,
          description:
            wallet.metadata?.description || `Algorand ${wallet.id} wallet`,
        };
      }) || []
    );
  }, [availableWallets]);
  const [connectingWallet, setConnectingWallet] = React.useState<string | null>(
    null
  );
  const [showWalletOptions, setShowWalletOptions] = useState(false);

  const handleConnect = async (walletId: string) => {
    if (connecting) return;
    setConnectingWallet(walletId);
    try {
      await connectWallet(walletId);
    } finally {
      setConnectingWallet(null);
    }
  };

  const handleAuthenticate = async () => {
    if (!accounts[0]) return;
    await authenticateWallet(accounts[0]);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Wavy background animation - bottom waves */}
      <div className="wavy-background">
        <div className="wave wave1"></div>
        <div className="wave wave2"></div>
        <div className="wave wave3"></div>

        {/* Top waves - flipped vertically */}
        <div className="wave wave-top wave1"></div>
        <div className="wave wave-top wave2"></div>
        <div className="wave wave-top wave3"></div>
      </div>
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-2">
          <div className="inline-flex items-center justify-center w-48 h-48 mb-6 rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-purple-500 p-0.5">
            <div className="w-full h-full rounded-full overflow-hidden bg-white dark:bg-gray-800 p-1">
              <img
                src="/images/Agent.png"
                alt="Agent Icon"
                className="w-full h-full object-cover rounded-full"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Algorand Agency
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Agentic Platform for Algorand Ecosystem
          </p>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            <Typewriter
              words={[
                "Algorand Agency",
                "is a platform for AI agents",
                "focused on Algorand blockchain & ecosystem",
                "By connecting your wallet",
                " you can authenticate and interact with various AI agents",
                "designed to assist you with everything Algorand",
                " various tasks and functionalities including:",
                "Agentic commerce and trading",
                " send, receive and swap funds ",
                "and assets or coding and development guidance!",
                "No subscription fees,",
                " Just pay as you go with GoPlausible AI credits!",
              ]}
              loop={0}
              cursor={true}
              cursorStyle="|"
              typeSpeed={70}
              deleteSpeed={10}
              delaySpeed={1000}
            />
          </p>

          {/* <Marquee 
            gradient={false} 
            speed={50} 
            className="text-blue-600 dark:text-blue-400 font-semibold"
          >
            AI-Powered Agents &nbsp; • &nbsp; Seamless Wallet Integration &nbsp; • &nbsp; Personalized Assistance
          </Marquee> */}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-200 dark:border-gray-700">
          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-sm flex items-center justify-between">
              <span>{error}</span>
              <button
                onClick={clearError}
                className="ml-2 text-red-800 dark:text-red-300 hover:underline"
              >
                Dismiss
              </button>
            </div>
          )}

          <div className="space-y-4">
            {!activeWallet && !accounts.length && (
              <>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center mb-4">
                    <img
                      src="/images/goPlausible-logo-type-v.png"
                      alt="Algorand Agency Logo"
                      className="h-16 w-auto"
                    />
                  </div>

                  <button
                    onClick={() => setShowWalletOptions(!showWalletOptions)}
                    className="flex items-center justify-center space-x-2 mx-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  >
                    <Wallet size={20} />
                    <span>
                      {!showWalletOptions
                        ? "Connect Your Wallet"
                        : "Hide wallet options"}
                    </span>
                    {showWalletOptions ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </button>

                  {!showWalletOptions && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg p-3 mt-4 mb-6">
                      Only Algorand wallet accounts with a{" "}
                      <a
                        href="https://goplausible.xyz"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                      >
                        GoPlausible
                      </a>{" "}
                      profile can sign in! You will be asked to create one by a
                      signature in next steps if you don't have one. 
                      <br />
                      <br />
                      <span className="font-semibold">
                         Those will never be used for signing agent transactions. Agent wallet accounts are separate and bound to your connected wallet account!
                      </span>
                    </div>
                  )}
                </div>

                {showWalletOptions && (
                  <div className="space-y-3 animate-fadeIn transition-all duration-300">
                    {/* <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 text-center">
                      Choose a wallet to connect
                    </h2> */}
                    {walletOptions.map((wallet) => {
                      return (
                        <button
                          key={wallet.id}
                          onClick={() => handleConnect(wallet.id as string)}
                          disabled={connecting || activeWallet === wallet.id}
                          className="w-full py-3 px-4 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition-all flex items-center space-x-3 text-gray-900 dark:text-white font-medium border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 disabled:opacity-75 disabled:cursor-not-allowed disabled:hover:border-gray-200 dark:disabled:hover:border-gray-600 group"
                        >
                          <div className="w-10 h-10 rounded-full bg-white dark:bg-gray-600 p-1.5 flex items-center justify-center overflow-hidden border border-gray-200 dark:border-gray-500">
                            {wallet.icon ? (
                              <img
                                src={wallet.icon}
                                alt={`${wallet.name} logo`}
                                className="w-full h-full object-contain rounded-full"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                {wallet.name.charAt(0)}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 text-left">
                            <div className="font-medium">
                              {connectingWallet === wallet.id
                                ? "Connecting..."
                                : wallet.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300">
                              {wallet.description}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {activeWallet && accounts.length > 0 && (
              <div className="space-y-6">
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Connected Wallet
                    </h3>
                    <p className="text-gray-900 dark:text-white font-medium break-all">
                      {formatWalletAddress(accounts[0])}
                    </p>
                  </div>

                  {balance !== undefined && minBalance !== undefined && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <span className="text-sm text-gray-500 dark:text-gray-400 block mb-1">
                          Balance
                        </span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {(balance / 1e6).toFixed(6)} ALGO
                        </span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500 dark:text-gray-400 block mb-1">
                          Available
                        </span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {((balance - minBalance) / 1e6).toFixed(6)} ALGO
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col space-y-3">
                    <button
                      onClick={handleAuthenticate}
                      disabled={authenticating}
                      className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors font-medium"
                    >
                      {authenticating
                        ? "Authenticating..."
                        : `Authenticate using ${formatWalletAddress(
                            accounts[0]
                          )}`}
                    </button>

                    <button
                      onClick={disconnectWallet}
                      className="w-full py-2 px-3 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded-lg transition-colors text-sm font-medium"
                    >
                      Disconnect Wallet
                    </button>
                  </div>
                </div>

                <div className="text-sm text-gray-500 dark:text-gray-400">
                  By authenticating, you agree to create a GoPlausible profile
                  that enables AI interactions.
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          By connecting your wallet, you agree to GoPlausible's{" "}
          <Link
            to="/terms"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link
            to="/privacy"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
};
